import { IMAGES, BENEFITS } from "@/lib/data";
import { SectionHeading, Overline } from "@/components/Section";
import { CtaBanner } from "@/pages/Home";
import { Check } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const ABOUT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "About Hylas Companion Trucking",
  "url": "https://hylascompanion.com/about",
  "description": "Hylas Companion runs OTR freight across all 48 states with new trucks and top driver pay. Driver-first fleet with company, lease, and local positions.",
  "mainEntity": {
    "@type": "Organization",
    "name": "Hylas Companion",
    "url": "https://hylascompanion.com",
    "telephone": "(708) 395-9565",
    "email": "contact@hylascompanion.com",
  },
};

export default function About() {
  return (
    <div data-testid="about-page">
      <SEOHead
        title="About Hylas Companion | CDL Truck Driver Recruiting Nationwide"
        description="Hylas Companion runs OTR freight across all 48 states with new trucks and top driver pay. Learn about our driver-first fleet operating routes from Chicago, IL to Denver, CO, Baltimore, MD, and beyond."
        keywords="trucking company driver, CDL-A fleet, OTR trucking company, truck driver recruiter, Hylas Companion about"
        path="/about"
        schema={ABOUT_SCHEMA}
      />
      <section className="pt-36 md:pt-44 pb-16 max-w-7xl mx-auto px-6 md:px-12">
        <Overline>About Hylas Companion</Overline>
        <h1 className="font-head text-4xl md:text-6xl font-bold uppercase tracking-[0.15em] mt-5 max-w-4xl leading-tight">
          A Fleet That Puts Drivers First
        </h1>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 grid lg:grid-cols-2 gap-14 items-center">
        <img src={IMAGES.bluePeterbilt} alt="Hylas Companion Peterbilt fleet truck — CDL-A driver positions available" className="w-full h-[420px] object-cover border border-border" loading="lazy" />
        <div>
          <p className="text-lg text-foreground/75 leading-relaxed">
            Hylas Companion runs freight across all 48 states, coast to coast, with the newest trucks on the road.
            We built this fleet around one idea: drivers do the hard part, so drivers should earn the most and be treated the best.
          </p>
          <p className="mt-6 text-lg text-foreground/75 leading-relaxed">
            From Company OTR seats with zero truck expenses to Lease Purchase paths toward ownership,
            we match every CDL-A driver to the lane that fits their life and their goals — with flexible home time and steady, quality freight.
          </p>
          <p className="mt-6 text-lg text-foreground/75 leading-relaxed">
            Our routes service major lanes including Chicago and Joliet, IL, Denver, CO, Baltimore, MD, West Jefferson, OH, Phoenix, AZ, West Memphis, AR, Oklahoma, and all points between. We haul for retail accounts including Kroger, Walmart, Dollar Tree, Family Dollar, and Target.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {["No hiring area restriction", "Live + dedicated lanes", "Weekly on-time pay", "New, reliable equipment"].map((x) => (
              <div key={x} className="flex items-center gap-3 text-foreground/80">
                <Check className="w-5 h-5 text-primary shrink-0" /> {x}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Hylas Different — plain prose for AI engines */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24" data-testid="about-difference">
        <div className="border-t border-border pt-14 grid lg:grid-cols-[280px_1fr] gap-12 items-start">
          <div>
            <span className="font-head text-[10px] uppercase tracking-[0.4em] text-primary">What Sets Us Apart</span>
            <h2 className="font-head text-3xl md:text-4xl font-bold uppercase tracking-[0.1em] mt-4 leading-tight">
              The Hylas<br />Difference
            </h2>
          </div>
          <div className="space-y-6 text-foreground/75 leading-relaxed text-base">
            <p>
              Hylas Companion is a recruitment and placement company. We run the Hylas Market — a platform where partner motor carriers compete for qualified drivers. Every driver we place is matched to a vetted partner carrier, and our recruiters remain accountable for what happens after placement, not just before it.
            </p>
            <p>
              On pay: company drivers start at 85¢ per mile or higher, with team seats at $1 CPM each. Lease purchase drivers run at 88% of gross freight revenue with no balloon payment at the end of the term — when the lease is paid, the truck is yours. Local W2 drivers earn up to $2,500 per week. Every settlement is itemized line by line. There are no mystery deductions.
            </p>
            <p className="text-sm text-foreground/50 italic">
              Figures are examples or ranges, not guarantees. Actual pay depends on the carrier and the driver's situation.
            </p>
            <p>
              On equipment: every truck in our fleet is a 2020–2026 model — Peterbilt 579 Ultraloft, Freightliner Cascadia, Kenworth T680 Next Gen, Volvo VNL 760/860, or International LT625. Standard issue includes an APU, 12V refrigerator, 2,000W inverter, and the Opti Idle system. No upgrade fees, no nickel-and-diming.
            </p>
            <p>
              On home time: OTR drivers typically run 2–3 weeks out and reset for 2–4 days. Regional and dedicated positions offer weekly home time. Local drivers are home daily. We don't promise what we can't deliver — every home time expectation is confirmed per program before you ever attend orientation.
            </p>
            <p>
              What we don't do: we don't charge fees to drivers, we don't use gimmick sign-on bonuses with 12-month clawback clauses, and we don't ghost applicants. Recruiters call — usually the same business day you apply. That's the whole model.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-muted py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading overline="Our Promise" title="What You Can Count On" center />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border mt-14 border border-border">
            {BENEFITS.map((b, i) => (
              <div key={b.title} className="bg-white p-8 md:p-10">
                <span className="font-head text-xs tracking-[0.3em] text-primary">0{i + 1}</span>
                <h3 className="font-head text-xl uppercase tracking-[0.1em] mt-4">{b.title}</h3>
                <p className="mt-3 text-foreground/70 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
